import { ElementRef, Injectable, NgZone } from '@angular/core';
import { Chart } from 'chart.js';
import { BehaviorSubject, Observable } from 'rxjs';
import {
	ReportBrandKitService,
	ReportBrandKitView
} from './report-brand-kit.service';
import './report-chart-theme';
import {
	ReportTheme,
	ReportThemeId,
	reportTheme,
	reportThemeCssVars
} from './report-themes';

export interface ReportBrandMark {
	logoSrc: string | null;
	agencyName: string | null;
}

const THEME_ATTRIBUTE = 'data-report-theme';

// CONTRACT: provided by each surface that draws a report — the editor, the PDF
// export page, the share page and present mode — in its own `providers`, so
// the host it paints is that surface's root and every slide created under it,
// by a template or by the slide host, inherits the theme.
@Injectable()
export class ReportThemeService {
	private readonly markState = new BehaviorSubject<ReportBrandMark | null>(
		null
	);
	readonly brandMark$: Observable<ReportBrandMark | null> =
		this.markState.asObservable();

	private current: ReportTheme = reportTheme(null);
	private appliedVars: string[] = [];

	constructor(
		private host: ElementRef<HTMLElement>,
		private zone: NgZone,
		private brandKits: ReportBrandKitService
	) {}

	get theme(): ReportTheme {
		return this.current;
	}

	apply(
		themeId: ReportThemeId | null | undefined,
		accent: string | null
	): void {
		const root = this.host.nativeElement;
		const next = reportTheme(themeId);
		const changed = next.id !== this.current.id;
		const vars = reportThemeCssVars(next, accent);

		this.appliedVars
			.filter(name => !(name in vars))
			.forEach(name => root.style.removeProperty(name));
		Object.entries(vars).forEach(([name, value]) =>
			root.style.setProperty(name, value)
		);
		this.appliedVars = Object.keys(vars);
		if (next.tokens) {
			root.setAttribute(THEME_ATTRIBUTE, next.id);
		} else {
			root.removeAttribute(THEME_ATTRIBUTE);
		}
		this.current = next;

		if (changed) {
			this.redrawCharts();
		}
	}

	setBrandKit(view: ReportBrandKitView | null | undefined): void {
		this.markState.next(
			view
				? {
						logoSrc: view.logoPath
							? this.brandKits.logoSrc(view.logoPath)
							: null,
						agencyName: view.agencyName
				  }
				: null
		);
	}

	// PERF: the serif is split into one file per script, and only the ones the
	// sample's text needs are fetched — a Latin report loads one of 51KB.
	loadFonts(sample: string): Promise<void> {
		const font = this.current.tokens?.font;
		if (!font || !document.fonts) {
			return Promise.resolve();
		}
		return document.fonts.load(`400 16px ${font}`, sample || 'Aa').then(
			() => undefined,
			() => undefined
		);
	}

	private redrawCharts(): void {
		const root = this.host.nativeElement;
		this.zone.runOutsideAngular(() => {
			Object.values(Chart.instances).forEach(chart => {
				if (chart.canvas && root.contains(chart.canvas)) {
					chart.update('none');
				}
			});
		});
	}
}
